'use client';

import React from 'react';
import Eyebrow from './Eyebrow';
import Heading from './Heading';
import Text from './Text';
import { SignupCtaGroup, SignupNote } from './SignupCta';
import type { CtaTarget } from '@/lib/signupMode';
import { cn } from '@/lib/twUtils';

export interface CtaPanelProps {
  eyebrow?: string;
  title: React.ReactNode;
  text?: React.ReactNode;
  /**
   * Seconda CTA della pagina: compare solo quando la modalità di
   * registrazione non ne impone già una (es. «Prova gratis» accanto alla demo).
   */
  secondary?: CtaTarget;
  /** Riga di rassicurazione sotto i bottoni; senza, quella della modalità */
  note?: string;
  /**
   * Bottone proprio al posto delle CTA della modalità di registrazione: per le
   * pagine che portano sempre allo stesso posto (es. /invito, alla registrazione).
   */
  action?: React.ReactNode;
  id?: string;
  className?: string;
}

/** Pannello navy di chiusura pagina: stesso blocco su tutte le pagine. */
const CtaPanel = ({
  eyebrow,
  title,
  text,
  secondary,
  note,
  action,
  id,
  className,
}: CtaPanelProps) => (
  <div
    id={id}
    className={cn(
      'relative overflow-hidden rounded-ds-2xl bg-primary p-[clamp(2.25rem,5vw,4rem)] text-center',
      className
    )}
  >
    <div
      aria-hidden
      className="pointer-events-none absolute -top-[7.5rem] -right-[7.5rem] h-[26rem] w-[26rem] rounded-full bg-[radial-gradient(circle,rgb(234_88_12/0.32),transparent_66%)]"
    />
    <div className="relative">
      {eyebrow && <Eyebrow className="mb-4">{eyebrow}</Eyebrow>}
      {/* 24ch, non 20: misurata perché ogni titolo di chiusura in uso stia
          entro due righe — il più lungo è quello di /chi-siamo. */}
      <Heading as="h2" size="lg" tone="light" className="mx-auto max-w-[24ch]">
        {title}
      </Heading>
      {text && (
        <Text size="intro" tone="onDark" className="mx-auto mt-4.5 max-w-[54ch]">
          {text}
        </Text>
      )}
      {action ? (
        <div className="mt-8 flex justify-center">{action}</div>
      ) : (
        <SignupCtaGroup on="dark" fallback={secondary} className="mt-8 justify-center" />
      )}
      {note ? (
        <Text size="note" tone="onDarkSubtle" className="mt-4.5">
          {note}
        </Text>
      ) : (
        <SignupNote tone="onDarkSubtle" className="mt-4.5" />
      )}
    </div>
  </div>
);

export default CtaPanel;
